import { concat } from 'lodash';

import { EntityPickerSettingsFilterBy } from '@work4all/data/lib/settings/settings';

import {
  DataRequest,
  Filters,
  KeysArguments,
  SortDirection,
} from '@work4all/models/lib/DataProvider';
import { Filter } from '@work4all/models/lib/DataProvider/filters';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileData } from '@work4all/models/lib/File';

import { EntityId, EntityLike } from '../../types';

// Manipulation of Id is done to have more then 1 entity in the single picker (businessPartner)
function enrichId<T extends EntityLike>(id: EntityId, entity: T): EntityId {
  if (typeof entity === 'string') return entity;

  // Parsing to string to avoid different types, but same values as type is lost sometimes
  return `${id}`;
}

// Factory function
export function makeGetEntityId(type: Entities) {
  return function <T extends EntityLike>(entity: T): EntityId {
    const id = getEntityId(entity);
    return enrichId(id, entity);
  };
}

export function getEntityId<T extends EntityLike>(entity: T): EntityId {
  if (typeof entity === 'string') return entity;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return entity?.id ?? (entity as any)?.data?.id;
}

export function toSelectionValue<T>(value: T | T[] | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return [value];
}

export type IPrepareRequestOptions = {
  entity: Entities;
  query: string;
  prefilter: Filters;
  data: unknown;
  filterBy: string | string[];
  searchPrefilter?: Filters;
  settingsFilter?: Filter;
  sortBy: string;
  sortByDirection?: SortDirection;
  searchHistory: FileData[];
  skip: boolean;
  vars: Record<string, unknown>;
  showFavorites?: boolean;
  keysArguments?: KeysArguments;
  completeDataResponse: boolean;
  /** Optional GraphQL query name override */
  queryName?: string;
};
export function prepareRequest({
  entity,
  query,
  prefilter,
  data,
  filterBy,
  searchPrefilter,
  settingsFilter,
  sortBy,
  sortByDirection = SortDirection.ASCENDING,
  searchHistory,
  skip,
  vars,
  showFavorites,
  keysArguments,
  completeDataResponse,
  queryName,
}: IPrepareRequestOptions): DataRequest {
  const initialPrefilter =
    searchPrefilter && query ? searchPrefilter : prefilter;
  const filter = showFavorites ? [] : initialPrefilter;

  const filterProperties = filterBy instanceof Array ? filterBy : [filterBy];
  const filterExpressions =
    query === ''
      ? []
      : filterProperties.map((field) => ({
          [field]: { $eq: `%${query}%` },
        }));
  const searchFilter: Filter =
    filterExpressions.length > 0 ? { $or: filterExpressions } : null;

  const historyFilter: Filter =
    searchHistory.length > 0 && query === '' && !showFavorites
      ? { id: { $in: searchHistory.map((item) => item.id) } }
      : null;

  const finalFilter: Filters = concat(
    ...([
      filter,
      !completeDataResponse && searchFilter,
      historyFilter,
      settingsFilter,
    ].filter(Boolean) as any) // TECH_DEBT
  );

  return {
    skip,
    entity,
    data,
    vars,
    sort: [{ field: sortBy, direction: sortByDirection }],
    filter: finalFilter,
    keysArguments,
    completeDataResponse,
    queryName,
  };
}

export function prepareSettingsFilters(
  settingsFilters?: EntityPickerSettingsFilterBy[],
  prefix?: string
) {
  if (!settingsFilters?.length) return null;
  return {
    $or: settingsFilters.map((settingFilter) => {
      const key = prefix ? `${prefix}.${settingFilter.key}` : settingFilter.key;
      return { [key]: settingFilter.query };
    }),
  };
}

// Similar to prepareSettingsFilters but works on already-formed Filters arrays.
// Specifically needed for pickers that have multiple tabs filtering the same entity differently
export function prepareTabsFilters(filters: Filters, prefix: string): Filters {
  if (!filters || !prefix) return filters;

  const prefixField = (field: string) => `${prefix}.${field}`;

  return filters.map((rule) => {
    if (Array.isArray(rule)) {
      return prepareTabsFilters(rule, prefix);
    } else if (typeof rule === 'object' && rule !== null) {
      const newRule = {};
      for (const key in rule) {
        if (key === '$or' || key === '$and' || key === '$nor') {
          newRule[key] = prepareTabsFilters(rule[key], prefix);
        } else if (key.startsWith('$')) {
          newRule[key] = rule[key];
        } else {
          newRule[prefixField(key)] = rule[key];
        }
      }
      return newRule;
    }
    return rule;
  });
}

export function filterOutById<T extends EntityLike>(
  list: T[],
  remove: T[]
): T[] {
  if (!remove.length) return list;

  const removeIds = new Set(remove.map((item) => getEntityId(item)));
  return list.filter((item) => !removeIds.has(getEntityId(item)));
}
